import type { TextValue } from '@/lib/content-standard/rich-text'

/**
 * C2C (City-to-City) page data types
 * Based on WAPI c2c.json endpoint response
 */

/**
 * Transport modes the C2C page route accepts — the single source of truth for code
 * that narrows on mode.
 *
 * The runtime list carries the vocabulary for code that must enumerate rather than
 * narrow (a Zod enum, a route-shape parse). Kept here, and kept import-free, so the
 * enum and the type cannot diverge.
 */
export const TRANS_MODES = ['bus', 'train', 'ferry', 'flight'] as const

export type TransMode = (typeof TRANS_MODES)[number]

/**
 * Request parameters for c2c.json endpoint
 */
export interface C2CRequestParams {
    locale: string // e.g., "en-us"
    departRegion: string // e.g., "us-ny"
    departCity: string // e.g., "new-york"
    arriveRegion: string // e.g., "us-ma"
    arriveCity: string // e.g., "boston"
    transMode: TransMode
}

/**
 * City editorial image from WAPI (stored on S3)
 */
export interface C2CCityImage {
    url: string // Relative path, e.g. "cortex/cities/city-editorial/BOS-0.jpg"
    name: string
    size?: number
    metadata?: {
        alt?: string
        author?: string
        license?: string
    }
}

/**
 * Location data for departure/arrival cities
 */
export interface C2CLocation {
    name: string
    countryCode: string
    countryName: string
    stateCode?: string
    stateName?: string
    wcityid: string
    placeId?: string
    latitude?: number
    longitude?: number
    timezone?: string
    content?: Record<string, string>
    /** The store copy's rendered values, keyed as `content`, which carries their plain text. */
    renderedContent?: Record<string, TextValue>
    images?: C2CCityImage[]
}

/**
 * Carrier gallery image from WAPI (stored on S3)
 */
export interface C2CCarrierImage {
    url: string // Relative path, e.g. "cortex/carriers/vehicles-gallery/USFLX-0.jpg"
    name: string
    size: number
    metadata?: {
        alt?: string
        author?: string
        license?: string
    }
}

/**
 * Carrier information
 */
export interface C2CCarrier {
    code: string
    carrierName: string
    carrierGuide: string | null // slug for carrier guide page; WAPI may return null
    content: Record<string, string>
    renderedContent?: Record<string, TextValue>
    metrics: {
        averageDailyTrips: number
        minPriceUsd: number
        averageDurationMinutes?: number
        averagePriceUsd?: number
    }
    images?: C2CCarrierImage[]
}

/**
 * Route metrics
 */
export interface C2CMetrics {
    cheapestPriceUsd: number
    /** route-wide average price (cortex avg_price_usd). */
    averagePriceUsd?: number
    /** cheapest carrier-average price (cortex min_avg_price_usd). */
    cheapestAveragePriceUsd: number
    savingsPriceUsd?: number
    minDurationMinutes: number
    tripsPerDay: number
    directTripsPerDay: number
    earliestDeparture: string
    latestDeparture: string
    numberOfCarriers: number
    minPriceUsd1?: number
    minPriceUsd2?: number
    minPriceUsdReverseLink?: number
    /** the reverse route's own slug, as the corpus records it. Authoritative: a slug
     *  built by swapping this route's endpoints can differ from it, and only this
     *  value is guaranteed to resolve. */
    reverseLink?: string
    /** carrier with the most scheduled departures (WAPI-derived, present in metrics). */
    mostOptionsCarrierName?: string
    /** busiest/quietest day — live WAPI serves {short, long} on metrics; a bare
     *  string form is tolerated for older fixtures. */
    busiestDayOfWeek?: string | { short: string; long: string }
    quietestDayOfWeek?: string | { short: string; long: string }
}

/**
 * Distance information
 */
export interface C2CDistance {
    mi: number
    km: number
}

/**
 * Travel frequency data
 */
export interface C2CTravelFrequency {
    busiestDayOfWeek: { short: string; long: string }
    quietestDayOfWeek: { short: string; long: string }
}

/**
 * Popular stations
 */
export interface C2CPopularStations {
    stations?: {
        departure: { name: string; code: string }[]
        arrival: { name: string; code: string }[]
    }
    arrive?: {
        main?: {
            code?: string // station wid — keys the media store (station/<code>.json)
            content: Record<string, string>
            renderedContent?: Record<string, TextValue>
            images?: C2CCityImage[]
        }
    }
    depart?: {
        main?: {
            code?: string // station wid — keys the media store (station/<code>.json)
            content: Record<string, string>
            renderedContent?: Record<string, TextValue>
            images?: C2CCityImage[]
        }
    }
}

/**
 * Review summary
 */
export interface C2CReviewSummary {
    overall: number
    reviewCount: number
    travelType?: number
}

/**
 * Individual review group (per carrier)
 */
export interface C2CReview {
    carrier_code?: string
    carrierGuide?: string | null
    stats: {
        reviewCarrierName?: string
        reviewCarrierOverall?: number
        reviewCarrierReviewCount?: number
    }
    individualReviews: {
        stats: {
            reviewIndividualFirstName?: string
            reviewIndividualDepartDate?: string
            reviewIndividualOverall?: number
            reviewIndividualPros?: string | null
            reviewIndividualCons?: string | null
            reviewIndividualTips?: string | null
            reviewIndividualTravelerType?: number | null
        }
        content?: {
            individualReviewCardOverall?: string
            individualReviewCardTravelerType?: string
        }
    }[]
}

/**
 * Blog link
 */
export interface C2CBlogLink {
    postTitle: string
    postUrl: string
    imageUrl?: string
}

/**
 * Alternative transportation mode statistics
 */
export interface C2CAltTransModeStats {
    cheapest?: string
    fastest?: string
    avg_bus_price_usd?: number
    avg_train_price_usd?: number
    avg_bus_duration?: number
    avg_train_duration?: number
    avg_daily_bus_trips?: number
    avg_daily_train_trips?: number
}

/**
 * A single data point in price history charts.
 * x is a date string (daily), month number (monthly), or days count (daysUntilDepart).
 */
export interface PriceHistoryPoint {
    x: string | number
    y: number
}

/**
 * Statistical metrics associated with price history data.
 */
export interface PriceHistoryMetrics {
    lowPriceMonth?: number
    highPriceMonth?: number
    lowPriceMonthPrice?: number
    highPriceMonthPrice?: number
    lowUpcomingWeekDate?: string
    highUpcomingWeekDate?: string
    lowUpcomingWeekPrice?: number
    highUpcomingWeekPrice?: number
    lowUpcomingMonthPrice?: number
    highUpcomingMonthPrice?: number
    lowUpcomingMonthPriceDate?: string
    highUpcomingMonthPriceDate?: string
    commonMonth?: number
    commonWeekPrice?: number
    commonMonthPrice?: number
    priceStability?: boolean
    priceStabilityMonth?: boolean
}

/**
 * Price history data from WAPI with three chart datasets and metrics.
 */
export interface C2CPriceHistory {
    daily?: PriceHistoryPoint[]
    monthly?: PriceHistoryPoint[]
    daysUntilDepart?: PriceHistoryPoint[]
    metrics?: PriceHistoryMetrics
}

/**
 * SEO meta information
 */
export interface C2CMeta {
    title?: string
    description?: string
    canonical?: string
}

/**
 * Full C2C response from WAPI
 */
export interface C2CResponse {
    carriers?: C2CCarrier[]
    content?: Record<string, string>
    /** The store copy overlay's rendered values, keyed as `content`, which carries each
     *  one's plain text: what a surface rendering rich text reads. */
    renderedCopy?: Record<string, TextValue>
    locations?: {
        arrival: C2CLocation
        departure: C2CLocation
    }
    stats?: {
        metrics?: C2CMetrics
        distance?: C2CDistance
        mostPopular?: C2CPopularStations
        travelFrequency?: C2CTravelFrequency
        pricing?: { bestPriceDaysOut?: number }
    }
    multimodal?: { type?: string | null; tripComposition?: string | null }
    meta?: C2CMeta
    priceHistory?: C2CPriceHistory
    carrierReviews?: C2CReview[]
    reviewSummary?: C2CReviewSummary
    timeTableDate?: string
    blogLinks?: C2CBlogLink[]
    altTransModeStats?: C2CAltTransModeStats
    no_index?: boolean
    no_index_locale?: string[] | null
    /**
     * IANA TZ id of the trip's origin city, resolved at the WAPI service
     * boundary from `locations.departure.timezone` (defaulting to 'UTC'
     * when WAPI omits the field). Optional on the type for test-fixture
     * ergonomics; real responses always have it set. Consumers should
     * default to 'UTC' when reading. Used downstream to format date
     * strings (e.g. review departure dates) in the trip's local TZ so
     * SSR and client hydration agree byte-for-byte — see
     * `invariants.md#ssr-safe-rendered-dates`.
     */
    originTimeZone?: string
}

/**
 * Service interface for C2C operations
 */
export interface WapiC2CService {
    getCityToCityData(
        params: C2CRequestParams,
    ): Promise<import('true-myth').Result<C2CResponse, Error>>
}
